import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import {
  CreateBusinessFailure,
  CreateBusinessPaymentFailure,
  CreateBusinessPaymentRequest,
  CreateBusinessPaymentSuccess,
  CreateBusinessRequest,
  CreateBusinessSuccess,
  ArchiveBusinessRequest,
  ArchiveBusinessSuccess,
  ArchiveBusinessFailure,
  DeleteBusinessDocumentFailure,
  DeleteBusinessDocumentRequest,
  DeleteBusinessDocumentSuccess,
  DeleteBusinessImageFailure,
  DeleteBusinessImageRequest,
  DeleteBusinessImageSuccess,
  DeleteBusinessPaymentFailure,
  DeleteBusinessPaymentRequest,
  DeleteBusinessPaymentSuccess,
  DeleteDocumentFailure,
  DeleteDocumentRequest,
  DeleteDocumentSuccess,
  DownloadBusinessDocumentRequest,
  DownloadBusinessPaymentFailure,
  DownloadBusinessPaymentRequest,
  DownloadBusinessPaymentSuccess,
  FetchBusinessDocumentsFailure,
  FetchBusinessDocumentsRequest,
  FetchBusinessDocumentsSuccess,
  FetchBusinessFailure,
  FetchBusinessPaymentsFailure,
  FetchBusinessPaymentsRequest,
  FetchBusinessPaymentsSuccess,
  FetchBusinessRequest,
  FetchBusinessSuccess,
  FetchBusinessesFailure,
  FetchBusinessesRequest,
  FetchBusinessesSuccess,
  FetchPropertyBuildingFailure,
  FetchPropertyBuildingRequest,
  FetchPropertyBuildingSuccess,
  GenerateBusinessDocumentFailure,
  GenerateBusinessDocumentRequest,
  GenerateBusinessDocumentSuccess,
  UpdateBusinessFailure,
  UpdateBusinessPaymentFailure,
  UpdateBusinessPaymentRequest,
  UpdateBusinessPaymentSuccess,
  UpdateBusinessRequest,
  UpdateBusinessSuccess,
  UploadBusinessImageFailure,
  UploadBusinessImageRequest,
  UploadBusinessImageSuccess,
  UploadDocumentFailure,
  UploadDocumentRequest,
  UploadDocumentSuccess,
} from './businesses.actions'
import { BusinessesService } from './businesses.service'
import * as Businesses from './businesses.types'
import { PropertiesService } from '@containers/properties/properties.service'
import {
  DownloadPropertyDocumentFailure,
  DownloadPropertyDocumentSuccess,
  GeneratePropertyDocumentFailure,
  GeneratePropertyDocumentSuccess,
} from '@containers/properties/properties.actions'
import { downloadBlob } from '@shared/shared.utils'

@Injectable()
export class BusinessesEffects {
  constructor(
    private actions: Actions,
    private businessesService: BusinessesService,
    private propertiesService: PropertiesService,
    private router: Router,
  ) {}

  FetchBusinesses$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.FETCH_BUSINESSES_REQUEST),
      switchMap((action: FetchBusinessesRequest) => {
        return this.businessesService.fetchBusinesses(action.payload).pipe(
          map((result) => {
            return new FetchBusinessesSuccess(
              result.data.results,
              result.data.filteredCount,
              result.data.totalCount,
            )
          }),
          catchError((err, caught) => {
            return of(new FetchBusinessesFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchPropertyBuilding$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.FETCH_PROPERTY_BUILDING_REQUEST),
      switchMap((action: FetchPropertyBuildingRequest) => {
        return this.propertiesService.fetchPropertyBuilding(action.id).pipe(
          map((result) => {
            return new FetchPropertyBuildingSuccess(result.data, action.target)
          }),
          catchError((err, caught) => {
            return of(new FetchPropertyBuildingFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  UpdateBusiness$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.UPDATE_BUSINESS_REQUEST),
      switchMap((action: UpdateBusinessRequest) => {
        return this.businessesService
          .updateBusiness(action.id, action.payload, action.options)
          .pipe(
            switchMap((result) => {
              return [new UpdateBusinessSuccess(result.data)]
            }),
            catchError((err, caught) => {
              return of(new UpdateBusinessFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  CreateBusiness$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.CREATE_BUSINESS_REQUEST),
      switchMap((action: CreateBusinessRequest) => {
        return this.businessesService.createBusiness(action.payload).pipe(
          switchMap((result) => {
            this.router.navigate(['/licenses', result.data.id])

            return [new CreateBusinessSuccess(result.data)]
          }),
          catchError((err, caught) => {
            return of(new CreateBusinessFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  ArchiveBusiness$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.ARCHIVE_BUSINESS_REQUEST),
      switchMap((action: ArchiveBusinessRequest) => {
        return this.businessesService.archiveBusiness(action.id).pipe(
          switchMap((result) => {
            this.router.navigate(['/licenses/archived'])
            return [new ArchiveBusinessSuccess(result.data)]
          }),
          catchError((err, caught) => {
            return of(new ArchiveBusinessFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchBusiness$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.FETCH_BUSINESS_DETAILS_REQUEST),
      switchMap((action: FetchBusinessRequest) => {
        return this.businessesService.fetchBusiness(action.id).pipe(
          switchMap((result) => {
            return result.data.propertyConnection?.propertyId
              ? this.propertiesService
                  .fetchPropertyBuilding(
                    result.data.propertyConnection?.propertyId,
                  )
                  .pipe(
                    switchMap((res) => {
                      return [new FetchBusinessSuccess(result.data, res.data)]
                    }),
                  )
              : [new FetchBusinessSuccess(result.data)]
          }),
          catchError((err, caught) => {
            return of(new FetchBusinessFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchBusinessDocuments$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.FETCH_BUSINESS_DOCUMENTS_REQUEST),
      switchMap((action: FetchBusinessDocumentsRequest) => {
        return this.businessesService.fetchBusinessDocuments(action.id).pipe(
          map((result) => {
            return new FetchBusinessDocumentsSuccess(result.data)
          }),
          catchError((err, caught) => {
            return of(new FetchBusinessDocumentsFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchBusinessPayments$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.FETCH_BUSINESS_PAYMENTS_REQUEST),
      switchMap((action: FetchBusinessPaymentsRequest) => {
        return this.businessesService.fetchBusinessPayments(action.id).pipe(
          map((result) => {
            return new FetchBusinessPaymentsSuccess(result.data)
          }),
          catchError((err, caught) => {
            return of(new FetchBusinessPaymentsFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  DownloadBusinessDocument$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.DOWNLOAD_BUSINESS_DOCUMENT_REQUEST),
      switchMap((action: DownloadBusinessDocumentRequest) => {
        return this.businessesService
          .downloadBusinessDocument(action.payload)
          .pipe(
            switchMap((result) => {
              return [new DownloadPropertyDocumentSuccess(result, null)]
            }),
            catchError((err, caught) => {
              return of(new DownloadPropertyDocumentFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  GenerateBusinessDocument$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.GENERATE_BUSINESS_DOCUMENT_REQUEST),
      switchMap((action: GenerateBusinessDocumentRequest) => {
        return this.businessesService
          .generateBusinessDocument(action.payload)
          .pipe(
            switchMap((result) => {
              return [new GenerateBusinessDocumentSuccess(result, null)]
            }),
            catchError((err, caught) => {
              return of(new GenerateBusinessDocumentFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  DeleteBusinessDocument$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.DELETE_BUSINESS_DOCUMENT_REQUEST),
      switchMap((action: DeleteBusinessDocumentRequest) => {
        return this.businessesService
          .deleteBusinessDocument(action.businessId)
          .pipe(
            switchMap((result) => {
              return [
                new DeleteBusinessDocumentSuccess(result, null),
                new FetchBusinessDocumentsRequest(action.businessId),
              ]
            }),
            catchError((err, caught) => {
              return of(new DeleteBusinessDocumentFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  CreatePayment$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.CREATE_BUSINESS_PAYMENT_REQUEST),
      switchMap((action: CreateBusinessPaymentRequest) => {
        return this.businessesService
          .createBusinessPayment(action.payload)
          .pipe(
            switchMap((result) => {
              return [new CreateBusinessPaymentSuccess(result.data)]
            }),
            catchError((err, caught) => {
              return of(new CreateBusinessPaymentFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  UpdatePayment$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.UPDATE_BUSINESS_PAYMENT_REQUEST),
      switchMap((action: UpdateBusinessPaymentRequest) => {
        return this.businessesService
          .updateBusinessPayment(action.payload)
          .pipe(
            switchMap((result) => {
              return [new UpdateBusinessPaymentSuccess(result.data)]
            }),
            catchError((err, caught) => {
              return of(new UpdateBusinessPaymentFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  DeletePayment$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.DELETE_BUSINESS_PAYMENT_REQUEST),
      switchMap((action: DeleteBusinessPaymentRequest) => {
        return this.businessesService
          .deleteBusinessPayment(action.payload)
          .pipe(
            switchMap((result) => {
              return [new DeleteBusinessPaymentSuccess(result.data)]
            }),
            catchError((err, caught) => {
              return of(new DeleteBusinessPaymentFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  DownloadPayment$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.DOWNLOAD_BUSINESS_PAYMENT_REQUEST),
      switchMap((action: DownloadBusinessPaymentRequest) => {
        return this.businessesService
          .downloadBusinessPayment(action.payload)
          .pipe(
            switchMap((result) => {
              return [new DownloadBusinessPaymentSuccess(result)]
            }),
            catchError((err, caught) => {
              return of(new DownloadBusinessPaymentFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  SavePaymentReceipt$ = createEffect(
    () =>
      this.actions.pipe(
        ofType<DownloadBusinessPaymentSuccess>(
          Businesses.DOWNLOAD_BUSINESS_PAYMENT_SUCCESS,
        ),
        tap((action) => downloadBlob(action.payload)),
      ),
    { dispatch: false },
  )

  UploadBusinessImage$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.UPLOAD_BUSINESS_IMAGE_REQUEST),
      switchMap((action: UploadBusinessImageRequest) => {
        return this.businessesService
          .uploadBusinessImage(action.id, action.payload)
          .pipe(
            switchMap((result) => {
              return [new UploadBusinessImageSuccess(result.data.images)]
            }),
            catchError((err, caught) => {
              return of(new UploadBusinessImageFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  DeleteBusinessImage$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.DELETE_BUSINESS_IMAGE_REQUEST),
      switchMap((action: DeleteBusinessImageRequest) => {
        return this.businessesService
          .deleteBusinessImage(action.id, action.imageTypeId)
          .pipe(
            switchMap((result) => {
              return [new DeleteBusinessImageSuccess(result.data.images)]
            }),
            catchError((err, caught) => {
              console.log(err)
              return of(new DeleteBusinessImageFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  UploadDocument$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.UPLOAD_DOCUMENT_REQUEST),
      switchMap((action: UploadDocumentRequest) => {
        return this.businessesService
          .uploadDocument(action.id, action.payload)
          .pipe(
            map((result) => {
              return new UploadDocumentSuccess(result.data.documents)
            }),
            catchError((err, caught) => {
              return of(new UploadDocumentFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  DeleteDocument$ = createEffect(() =>
    this.actions.pipe(
      ofType(Businesses.DELETE_DOCUMENT_REQUEST),
      switchMap((action: DeleteDocumentRequest) => {
        return this.businessesService.deleteDocument(action.id).pipe(
          map((result) => {
            return new DeleteDocumentSuccess(result.data.documents)
          }),
          catchError((err, caught) => {
            return of(new DeleteDocumentFailure({ error: err }))
          }),
        )
      }),
    ),
  )
}
